import { AnyAction } from 'redux';
import { AxiosPromise, AxiosResponse } from 'axios';
import { Operation, ServerResponse } from 'data/types';
import { ThunkAction } from 'redux-thunk';
import { RootState } from 'data/root.reducers';
import { FieldUpdateHandlerParam } from 'shared/form';

export const API_PATH_SIP_SETTING = '/sipsettings';

export const SET_SIP_SETTING_LOADING = 'SET_SIP_SETTING_LOADING';
export const GO_TO_SIP_SETTING_CREATE = 'GO_TO_SIP_SETTING_CREATE';
export const GO_TO_SIP_SETTING_EDIT = 'GO_TO_SIP_SETTING_EDIT';
export const GO_TO_SIP_SETTING_VIEW = 'GO_TO_SIP_SETTING_VIEW';
export const GET_SIP_SETTING = 'GET_SIP_SETTING';
export const GET_SIP_SETTING_PENDING = 'GET_SIP_SETTING_PENDING';
export const GET_SIP_SETTING_FULFILLED = 'GET_SIP_SETTING_FULFILLED';
export const UPDATE_SIP_SETTING = 'UPDATE_SIP_SETTING';
export const UPDATE_SIP_SETTING_FULFILLED = 'UPDATE_SIP_SETTING_FULFILLED';
export const UPDATE_SIP_SETTING_REJECTED = 'UPDATE_SIP_SETTING_REJECTED';
export const DELETE_SIP_SETTING = 'DELETE_SIP_SETTING';
export const DELETE_SIP_SETTING_FULFILLED = 'DELETE_SIP_SETTING_FULFILLED';
export const DELETE_SIP_SETTING_REJECTED = 'DELETE_SIP_SETTING_REJECTED';
export const SELECT_SIP_SETTING = 'SELECT_SIP_SETTING';
export const CREATE_SIP_SETTING = 'CREATE_SIP_SETTING';
export const CREATE_SIP_SETTING_FULFILLED = 'CREATE_SIP_SETTING_FULFILLED';
export const CREATE_SIP_SETTING_REJECTED = 'CREATE_SIP_SETTING_REJECTED';
export const UPDATE_SIP_ERRORS = 'UPDATE_SIP_ERRORS';

export enum SipSettingPrivileges {
  View = 'dc_access',
  Edit = 'dc_access',
  Delete = 'dc_access',
}

export interface SipSetting {
  id: string,
  sipInfo: SipInfo,
  sipTimer: SipTimer,
  prefixes: SipPrefix[],
  networkElements: SipNetworkElements[]
}

export enum SipSettingSection {
  sipInfo = 'sipInfo',
  sipTimer = 'sipTimer',
}

export type SipInfo = {
  id: string,
  niid: string,
  port: number | string,
  encryptPani: string
  encryptKey: string,
  group: string,
  access: string
}

export type SipTimer = {
  t1: number
  t2: number
  t4: number
  tc: number
  td: number
};

export type SipPrefix = {
  id: string,
  pid: string,
  code: string
  operation?: Operation
};

export type SipNetworkElements = {
  id: string,
  nid: string,
  netid: string,
  ipAddr: string,
  port: number | string,
  netype: string,
  action?: string,
  operation?: Operation | undefined
};

export type SipSettingFieldUpdateCallback = (
  param: FieldUpdateHandlerParam,
) => void

export interface SipSettingState {
  entities: SipSetting[]
  selected: SipSetting[],
  single: SipSetting | null
  createdId: string | null
  ui: SipSettingsUIState
  sipSettingErrors: SipSettingErrors
}

export interface SipSettingErrors {
  sipInfo: { error: string, data: SipInfo}[],
  sipTimer: { error: string, data: SipTimer}[],
  prefixes: { error: string, data: SipPrefix}[],
  networkElements: { error: string, data: SipNetworkElements}[],
  hasError: boolean,
}

export interface SipSettingsUIState {
  isCreatePopupOpen: boolean
  isLoading: boolean
}

export interface SetSipSettingLoadingAction extends AnyAction {
  type: typeof SET_SIP_SETTING_LOADING
  payload: { isLoading: boolean }
}

export interface GetSipSettingAction extends AnyAction {
  type: typeof GET_SIP_SETTING
  payload: AxiosPromise<ServerResponse<SipSetting>>
}

export interface GetSipSettingPendingAction extends AnyAction {
  type: typeof GET_SIP_SETTING_PENDING
  payload: null
}

export interface GetSipSettingFulfilledAction extends AnyAction {
  type: typeof GET_SIP_SETTING_FULFILLED
  payload: AxiosResponse<ServerResponse<SipSetting>>
}

export interface UpdateSipSettingThunkAction extends ThunkAction<void, RootState, void, AnyAction> { }
export interface UpdateSipSettingAction extends AnyAction {
  type: typeof UPDATE_SIP_SETTING
  payload: AxiosPromise<ServerResponse<SipSetting>>
}

export interface UpdateSipSettingFulfilledAction extends AnyAction {
  type: typeof UPDATE_SIP_SETTING_FULFILLED
  payload: AxiosResponse<ServerResponse<SipSetting>>
}

export interface UpdateSipSettingRejectedAction extends AnyAction {
  type: typeof UPDATE_SIP_SETTING_REJECTED
  payload: AxiosResponse<ServerResponse<SipSetting>>
}

export interface DeleteSipSettingThunkAction extends ThunkAction<void, RootState, void, AnyAction> { }
export interface DeleteSipSettingAction extends AnyAction {
  type: typeof DELETE_SIP_SETTING
  payload: AxiosPromise<ServerResponse<SipSetting>>
}

export interface DeleteSipSettingFulfilledAction extends AnyAction {
  type: typeof DELETE_SIP_SETTING_FULFILLED
  payload: AxiosResponse<ServerResponse<SipSetting>>
}

export interface DeleteSipSettingRejectedAction extends AnyAction {
  type: typeof DELETE_SIP_SETTING_REJECTED
  payload: AxiosResponse<ServerResponse<SipSetting>>
}

export interface CreateSipSettingThunkAction extends ThunkAction<void, RootState, void, AnyAction> { }
export interface CreateSipSettingAction extends AnyAction {
  type: typeof CREATE_SIP_SETTING
  payload: AxiosPromise<ServerResponse<SipSetting>>
}

export interface CreateSipSettingFulfilledAction extends AnyAction {
  type: typeof CREATE_SIP_SETTING_FULFILLED
  payload: AxiosResponse<ServerResponse<SipSetting>>
}

export interface CreateSipSettingRejectedAction extends AnyAction {
  type: typeof CREATE_SIP_SETTING_REJECTED
  payload: AxiosResponse<ServerResponse<SipSetting>>
}
export interface SelectSipSettingAction extends AnyAction {
  type: typeof SELECT_SIP_SETTING
  payload: SipSetting[]
}

export interface UpdateSipErrors extends AnyAction {
  type: typeof UPDATE_SIP_ERRORS
  payload: SipSettingErrors
}

export type SipSettingActionTypes =
  SetSipSettingLoadingAction |
  GetSipSettingAction |
  GetSipSettingPendingAction |
  GetSipSettingFulfilledAction |
  UpdateSipSettingAction |
  UpdateSipSettingFulfilledAction |
  UpdateSipSettingRejectedAction |
  DeleteSipSettingAction |
  DeleteSipSettingFulfilledAction |
  DeleteSipSettingRejectedAction |
  SelectSipSettingAction |
  CreateSipSettingAction |
  CreateSipSettingFulfilledAction |
  CreateSipSettingRejectedAction |
  UpdateSipErrors;
